import { apiFetch, apiFetchJson } from '@wundo/client/api.ts';
import type {
  BroadcastHistoryItem,
  BroadcastRequest,
  BroadcastResponse,
  CreateTemplateRequest,
  Template,
} from '@/types/broadcast.ts';
import type { PaginatedResponse } from '@/types/pagination.ts';
import { getUser } from './users.ts';
import { PAGE_SIZE } from '@/lib/pageSize.ts';

/**
 * The message templates a broadcast is sent from.
 *
 * Paged server-side: the list is short today, but the endpoint has always
 * answered in pages and the console has always asked for one.
 */
export async function listTemplates(
  page = 1,
  limit = PAGE_SIZE,
): Promise<PaginatedResponse<Template>> {
  return apiFetchJson<PaginatedResponse<Template>>(
    `/templates?page=${page}&limit=${limit}`,
  );
}

/** One template by name — the history detail reads the body a broadcast was sent with. */
export async function getTemplate(name: string): Promise<Template> {
  return apiFetchJson<Template>(`/templates/${encodeURIComponent(name)}`);
}

/** One endpoint writes both: the name is the key, so posting an existing one replaces its body. */
export async function createOrUpdateTemplate(
  request: CreateTemplateRequest,
): Promise<Template> {
  return apiFetchJson<Template>('/templates', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/** There is no undo. */
export async function deleteTemplate(name: string): Promise<void> {
  await apiFetch(`/templates/${encodeURIComponent(name)}`, { method: 'DELETE' });
}

/**
 * Sends one template to a list of people, and answers with how it went.
 *
 * A partial failure is still a 200: the counts and the error lines in the body
 * are the result, not the status code.
 */
export async function sendBroadcast(
  request: BroadcastRequest,
): Promise<BroadcastResponse> {
  return apiFetchJson<BroadcastResponse>('/broadcast', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/** Every broadcast that has been sent, newest first. */
export async function listBroadcasts(
  page = 1,
  limit = PAGE_SIZE,
): Promise<PaginatedResponse<BroadcastHistoryItem>> {
  return apiFetchJson<PaginatedResponse<BroadcastHistoryItem>>(
    `/broadcast?page=${page}&limit=${limit}&sort_by=created_at&sort_order=desc`,
  );
}

/**
 * The little of a person the history needs: who sent a broadcast, and who
 * received it. A broadcast record keeps ids alone.
 */
export interface UserSummary {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

/**
 * One recipient, or the id back when they can no longer be read.
 *
 * A deleted user must not take the whole panel down with them — a broadcast to
 * forty people is still worth reading when one of them has since gone.
 */
export async function fetchUserSummary(id: string): Promise<UserSummary> {
  return getUser(id).catch(() => ({
    id,
    first_name: null,
    last_name: null,
    email: null,
  }));
}
